import Header from "./shared/header";
import Footer from "./shared/footer";
import MobileLayout from "./shared/mobile_layout";
import { useState, useEffect } from "react";

import DesktopLayout from "./shared/desktop_layout";
import FeaturesSection from "./shared/outcomesWeDeliver/featuresSection";
import CaseStudies from "./shared/outcomesWeDeliver/CaseStudies";

function OperationalCost() {
  const features = [
    {
      title: "Platform Modernization",
      description:
        "We help clients move away from legacy infrastructure to more elastic, efficient, and cost-effective platforms.",
      image: "https://via.placeholder.com/200x150",
      bullets: [
        { from: "VMware", to: "AWS EC2 or AWS Colo (Hyper-V)" },
        { from: "Teradata", to: "Redshift, S3, Glue" },
        { from: "Oracle", to: "Databricks" },
        { from: "Cloudera", to: "SageMaker or Bedrock" },
        {
          note: "Migrations are accelerated using agentic tools including code converters, schema mappers, and validation agents.",
        },
      ],
    },
    {
      title: "Pipeline & ML Ops Optimization",
      description:
        "We consolidate fragmented MLOps tooling and automate repetitive pipeline engineering.",
      image: "https://via.placeholder.com/200x150",
      bullets: [
        { from: "Informatica", to: "Glue" },
        { from: "Cloudera DS", to: "SageMaker" },
        {
          note: "Auto-documentation, test generation, rollback scaffolding — the result: faster time to production and reduced reliance on niche platform licenses.",
        },
      ],
    },
    {
      title: "Digital Coworkers for Ops & Data Teams",
      description:
        "Our AI coworkers automate repeatable workflows so humans can focus on judgment, design, and impact.",
      image: "https://via.placeholder.com/200x150",
      bullets: [
        { note: "ETL / ELT" },
        { note: "Data validation & QA" },
        { note: "Marketing Ops, Finance Ops, IT Service Requests" },
        {
          note: "These agents don’t replace people — they remove drudgery.",
        },
      ],
    },
    {
      title: "Cloud Cost Optimization",
      description:
        "We analyze AWS usage patterns to reduce waste and improve ROI.",
      image: "https://via.placeholder.com/200x150",
      bullets: [
        { note: "Serverless design reviews" },
        { note: "Data tiering and storage audit" },
        {
          note: "Agent-led tagging, anomaly alerts, and cost observability dashboards",
        },
      ],
    },
  ];

  const caseStudiesCards = [
    {
      text: "Streamlining 13PB Data Migration with a Unified Cloud Platform",
      description:
        "AI-driven modernization of legacy pipelines enabled faster ETL, lower costs, and real-time decisioning on a scalable cloud-native architecture.",
      image:
        "/images/revenue-growth/OBZ Case Study Images OutcomesOperational Cost.png",
      slug: "data-migration-with-cloud-platform",
    },
    {
      text: "Driving Retail Uplift with AI-Powered Shelf Analytics",
      description:
        "Used CCTV and ML to optimize product visibility and placement in-store, boosting revenue, reducing manual audits, and enabling real-time shelf insights.",
      image:
        "/images/revenue-growth/OBZ Case Study Images OutcomesOperational Cost2.png",
      slug: "retail-shelf-analytics-with-ai",
    },
    {
      text: "ML-Driven Network Planning to Optimize Capex & Churn",
      description:
        "Created a digital twin of a telecom network to identify churn hotspots and optimize tower investments, reducing cost and boosting ROI across 1,000+ sites.",
      image:
        "/images/revenue-growth/OBZ Case Study Images OutcomesOperational Cost3.png",
      slug: "network-planning-ml-capex-churn",
    },
  ];

  const [screenSize, setScreenSize] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => {
      setScreenSize(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const Layout = ({ children, backgroundColor }) => {
    return screenSize <= 768 ? (
      <MobileLayout backgroundColor={backgroundColor}>{children}</MobileLayout>
    ) : (
      <DesktopLayout backgroundColor={backgroundColor}>
        {children}
      </DesktopLayout>
    );
  };

  return (
    <div>
      <Layout>
        <Header color="rgba(11, 21, 76, 1)" />
      </Layout>
      <Layout backgroundColor="black">
        <div className="w-full mt-16 py-16 lg:py-10">
          <div className="w-[88%] lg:w-[80%] mx-auto">
            {/* Header Section */}
            <div className="mb-12 lg:mb-7">
              <h2 className="text-[32px] lg:text-[48px] xl:text-[56px] text-white RoobertLight leading-[120%] mb-4">
                Operational Cost & Efficiency
              </h2>
              <h3 className="text-[20px] lg:text-[24px] xl:text-[28px] text-white RoobertMedium leading-[120%] mb-8">
                Modernize your tech stack. Streamline operations. Scale without
                bloating cost.
              </h3>
            </div>

            {/* Body Text */}
            <div className="mb-12 lg:mb-8 space-y-5">
              <p className="text-[16px] lg:text-[18px] text-white RoobertRegular leading-[140%]">
                At OBZ, we partner with enterprise teams to reduce
                cost-to-serve, simplify data and ML operations, and eliminate
                redundant effort not by adding tools, but by reshaping how work
                gets done.
              </p>
              <p className="text-[16px] lg:text-[18px] text-white RoobertRegular leading-[140%]">
                Our approach is delivery-first: combining experienced
                consultants with AI coworkers to execute real transformations
                inside your AWS environment, aligned to your architecture, and
                measurable from day one.
              </p>
            </div>
            <div className="mb-4 lg:mb-5">
              <FeaturesSection features={features} />
            </div>
          </div>
        </div>
      </Layout>
      <Layout backgroundColor="black">
        <CaseStudies caseStudiesCards={caseStudiesCards} />
      </Layout>
      <Layout>
        <div className="w-full bg-fluoro-green py-16 lg:py-8 min-h-[500px] flex items-center justify-center relative">
          <div className="w-[88%] lg:w-[80%] py-5 px-5 h-full mx-auto relative bg-fluoro-green">
            <div className="mb-12 lg:mb-7 px-6">
              <h2 className="text-[32px] lg:text-[48px] xl:text-[56px] text-[#0A1931] RoobertLight leading-[120%] mb-4">
                Work Smarter Scale Faster
              </h2>
              <p className="text-[16px] lg:text-[18px] text-[#0A1931] font-bolder RoobertSemiBold leading-[140%] mb-4">
                We help enterprises reimagine how work gets done—without adding
                complexity. From platform modernization to digital coworkers,
                our solutions unlock cost efficiency and operating leverage by
                aligning people, process, and GenAI. Built on AWS and built to
                deliver, our frameworks turn transformation into tangible
                savings. 
              </p>
              <p className="text-[16px] lg:text-[18px] text-[#0A1931] font-bolder RoobertSemiBold leading-[140%] mb-4">
               → Ready to optimize your ops? Let’s get started. 
              </p>
              <div className="border-r-4 border-t-4 border-[#0A1931] absolute top-0 right-0 w-1/4 h-1/2"></div>
              <div className="border-r-4 border-t-4 border-[#0A1931] absolute bottom-0 rotate-180 left-0 w-1/4 h-1/2"></div>
            </div>
          </div>
        </div>
      </Layout>
      <Layout>
        <Footer />
      </Layout>
    </div>
  );
}

export default OperationalCost;
